import {toast} from '@wandb/weave/common/components/elements/Toast';
import {useCallback} from 'react';

import {useWBAgentContextStore} from '../WBAgentContextStore';
import {
  type ComposerImage,
  type FailedComposerImage,
  fileToImagePart,
  type ImagePartResult,
  type ImageValidationReason,
  MAX_IMAGE_BYTES,
  MAX_IMAGES_PER_TURN,
  MAX_TOTAL_IMAGE_BYTES,
  toComposerImage,
} from './imageParts';

type RejectionReason =
  | Extract<ImagePartResult, {ok: false}>['reason']
  | ImageValidationReason;

const MAX_IMAGE_MB = Math.round(MAX_IMAGE_BYTES / (1024 * 1024));
const MAX_TOTAL_IMAGE_MB = Math.round(MAX_TOTAL_IMAGE_BYTES / (1024 * 1024));

const REJECTION_MESSAGES: Record<RejectionReason, string> = {
  'unsupported-type': 'Unsupported image type. Use JPEG, PNG, GIF, or WebP.',
  'too-large': `Each image must be under ${MAX_IMAGE_MB} MB.`,
  'read-failed': 'Could not read that image. Try a different file.',
  'too-many': `You can attach up to ${MAX_IMAGES_PER_TURN} images.`,
  'total-too-large': `Those images exceed the total attachment size limit of ${MAX_TOTAL_IMAGE_MB} MB.`,
};

export function useComposerImageUpload() {
  const addComposerImages = useWBAgentContextStore(s => s.addComposerImages);
  const addFailedComposerImages = useWBAgentContextStore(
    s => s.addFailedComposerImages
  );

  const addFiles = useCallback(
    // `extraReasons` lets callers fold in rejections detected upstream (e.g.
    // react-dropzone filtering by `accept`) into the same toast pass.
    async (files: File[], extraReasons: RejectionReason[] = []) => {
      const results = await Promise.all(files.map(fileToImagePart));

      const accepted: ComposerImage[] = [];
      const failed: FailedComposerImage[] = [];
      // Dedupe by reason so multiple rejected files yield one toast per cause.
      const reasons = new Set<RejectionReason>(extraReasons);
      results.forEach((result, i) => {
        if (result.ok) {
          accepted.push(toComposerImage(result.part));
        } else if (result.reason === 'read-failed') {
          // We still have the file, so keep it so we can render it
          // as an error image thumbnail.
          failed.push({
            id: crypto.randomUUID(),
            previewUrl: URL.createObjectURL(files[i]),
          });
        } else {
          reasons.add(result.reason);
        }
      });

      if (accepted.length > 0) {
        const validation = addComposerImages(accepted);
        // The readonly gate never maps to a toast.
        if (!validation.ok && validation.reason !== 'readonly') {
          reasons.add(validation.reason);
        }
      }

      if (failed.length > 0) {
        addFailedComposerImages(failed);
      }

      for (const reason of reasons) {
        toast(REJECTION_MESSAGES[reason], {type: 'error'});
      }
    },
    [addComposerImages, addFailedComposerImages]
  );

  // react-dropzone `onDrop`: files filtered out by `accept` arrive as
  // `rejectedFiles` (never in `accepted`), so map them to `unsupported-type`.
  const onDrop = useCallback(
    (accepted: File[], rejectedFiles: File[]) => {
      const extraReasons: RejectionReason[] =
        rejectedFiles.length > 0 ? ['unsupported-type'] : [];
      void addFiles(accepted, extraReasons);
    },
    [addFiles]
  );

  return {onDrop};
}
